/**
 * MapPane — the LEFT pane of the evaluation workspace (spec §3.2 / §3.5).
 *
 * REUSE, NOT REINVENT. This pane mounts the EXISTING marking surface
 * (`StaffTechnicalPanel` in its `hideGridTool` L1A marking mode) — the same
 * working core ProjectDetail/L1AFlow already use. That panel owns:
 *   · the property-grid resolver (`resolvePropertyGridForMarking`),
 *   · the FloorBoundaryTracer (boundary LOCKED from the property grid),
 *   · the mode toolbar (Boundary / Mark entrance / Mark AUO),
 *   · the "Edit / replace working plan" crop → Save-Trace flow,
 *   · the AUO disposition surface + the inherited `saveFloors` autosave.
 * The workspace adds NO second marking engine and NO second save store.
 *
 * Phase 1 turns ON `enableZoom` (in-frame zoom/pan on the primary surface) and
 * passes `focusPropertyId` so the deep-linked property is the active candidate.
 *
 * NOTE (flagged to Larry): because the AUO disposition list is rendered INSIDE
 * StaffTechnicalPanel's marking surface (it shares that panel's mark state and
 * suggest/override/palette wiring), it stays in this left pane for Phase 1
 * rather than being physically relocated to the right RefPane. Lifting it out
 * means decomposing the 3.2k-line panel — a Phase-2 refactor that would risk the
 * verified Pass-1/2 checkpoint. The right pane carries the Phase-2 media
 * placeholder per scope.
 */
import StaffTechnicalPanel from '../StaffTechnicalPanel';
import type { PropertyRefLike } from '../../lib/candidatePropertyJoin';

export default function MapPane({
  clientId,
  projectId,
  propertyId,
  actor,
  isAdmin,
  intakeTier,
  linkedPropertyRefs,
  onMsg,
}: {
  clientId: string | null;
  projectId: string;
  /** the deep-linked registry property (P#####) to focus. */
  propertyId: string;
  actor: { uid: string | null; name: string };
  isAdmin: boolean;
  /** e.g. "L1A_R1" — drives the panel's existing L1A check (no hardcoded guard). */
  intakeTier?: string;
  /** the project's linked registry properties (for the candidate auto-join). */
  linkedPropertyRefs: PropertyRefLike[];
  onMsg: (m: string) => void;
}) {
  return (
    <div className="evalws-mappane" aria-label="Property map and marking">
      <StaffTechnicalPanel
        clientId={clientId}
        projectId={projectId}
        actor={actor}
        isAdmin={isAdmin}
        onMsg={onMsg}
        intakeTier={intakeTier}
        linkedPropertyIds={linkedPropertyRefs.map((r) => r.propertyId)}
        linkedPropertyRefs={linkedPropertyRefs}
        hideGridTool
        hideReportContext
        evalChecklist
        enableZoom
        focusPropertyId={propertyId}
        workspaceMode
      />
    </div>
  );
}
